'use client'

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiError, ehRedeBloqueada } from '@/lib/api/errors'
import { checkPermission } from '@/lib/permissions/check'
import { USERS_PERMISSIONS } from '@/lib/permissions/users'
import { useAuth } from '@/providers/AuthContext'
import { fetchPresence } from '@/services/users/presence'
import type { UserPresenceEntry } from '@/types/presence'

export const presenceKeys = { snapshot: ['users', 'presence'] as const }

/** Referência única do vazio — mapa novo por render re-renderizaria cada
 *  hover-card e cada linha do drawer. */
const NENHUMA: ReadonlyMap<string, UserPresenceEntry> = new Map()
const VAZIO: UserPresenceEntry[] = []

export interface PresenceSnapshotResult {
  byId: ReadonlyMap<string, UserPresenceEntry>
  isLoading: boolean
  isError: boolean
}

/**
 * `GET /users/presence` indexado por `db_id`. Eixo SEPARADO do roster de
 * identidade: outra rota, outra permission, outro ciclo de vida. Sem
 * `users:presence:read` nenhum request sai e o mapa fica vazio — e o vazio é o
 * caminho COMUM, não erro.
 *
 * `staleTime` de 5 min porque o ao-vivo vem do socket (`presence_changed` patcha
 * esta mesma key em `providers/PresenceContext`); o refetch é rede de segurança
 * pra quem perdeu evento offline, não a fonte.
 */
export function usePresenceSnapshot(): PresenceSnapshotResult {
  const { user } = useAuth()
  const podeVer = Boolean(user) && checkPermission(user?.permissions ?? [], USERS_PERMISSIONS.PRESENCE_READ)

  const query = useQuery<UserPresenceEntry[]>({
    queryKey: presenceKeys.snapshot,
    queryFn: async () => {
      try {
        return await fetchPresence()
      } catch (err) {
        // Grant revogado entre o gate e a resposta: ausência, não falha. O 403 de REDE não é
        // ausência: vira erro, porque o desbloqueio só refaz consulta em erro.
        if (err instanceof ApiError && err.status === 403 && !ehRedeBloqueada(err)) return []
        throw err
      }
    },
    enabled: podeVer,
    staleTime: 5 * 60_000,
  })

  const lista = podeVer ? (query.data ?? VAZIO) : VAZIO
  const byId = useMemo(
    () => (lista.length === 0 ? NENHUMA : new Map(lista.map((e) => [e.db_id, e]))),
    [lista],
  )

  return { byId, isLoading: query.isLoading, isError: query.isError }
}
